import React, { Component } from "react";
import { Row, Col } from "react-bootstrap";
import gql from "graphql-tag";
import * as Cookies from "es-cookie";
import { withApollo } from "react-apollo";
import { withRouter } from "react-router-dom";
import S from "./buildingFinanceStyles";
import { BuildingListShimmer } from "./buildingShimmer";
import AddBuildingModal from "./AddBuildingModal";
import { attachModuleGuard, AccessDeniedModal } from "../../../utils/moduleGuard";
import { scopeBranches, onBranchScopeChange } from "../../../utils/branchScope";

// /building-creation → "Hostel_Buildings". Match on module_key only.
// This dashboard opens AddBuildingModal (student→room allotment). No delete surface here.
const HOSTEL_BUILDINGS_MODULE = ["Hostel_Buildings"];

const GET_HOSTEL_ALLOCATIONS = gql`
  query($institute_id: Int, $branch_id: Int) {
    getHostelAllocations(institute_id: $institute_id, branch_id: $branch_id) {
      student_id
      student_name
      roll_no
      admission_no
      branch_name
      building_name
      floor_name
      room_name
    }
  }
`;

const th = {
  fontSize: 11,
  fontWeight: 700,
  color: "#475569",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  padding: "12px 14px",
  textAlign: "left",
  background: "#f1f5f9",
  borderBottom: "1px solid #e2e8f0",
  position: "sticky",
  top: 0,
  zIndex: 1,
};
const td = {
  fontSize: 13,
  color: "#1f2937",
  padding: "10px 14px",
  borderBottom: "1px solid #f1f5f9",
  verticalAlign: "middle",
};

class HostelDashboardSection extends Component {
  constructor(props) {
    super(props);
    this.state = {
      branchSearch: "",
      rows: [],
      loading: 0,
      search: "",
      page: 1,
      pageSize: 10,
      allocateModal: false,
      accessDeniedShow: false,
    };

    this.guard = attachModuleGuard(this, HOSTEL_BUILDINGS_MODULE);
  }

  // "Allocate Student" — opens AddBuildingModal, which owns the allotment mutation.
  // Gating the opener stops the write; the button stays visible and clickable.
  openAllocateModal = () => {
    if (!this.guard.requireEdit()) return;
    this.setState({ allocateModal: true });
  };

  componentDidMount() {
    this.fetchAllocations();
    // Branch comes from the header (NavbarOne) — reload when it changes.
    this.offBranchScope = onBranchScopeChange(() => this.fetchAllocations());
  }

  componentWillUnmount() {
    if (this.offBranchScope) this.offBranchScope();
  }

  // getHostelAllocations takes a single branch_id (0 = every branch), so fan the query
  // out across the header's branch scope and concatenate.
  fetchAllocations = async () => {
    const branchIds = scopeBranches(this.props.globals?.globalBranches || []).map((b) => parseInt(b.id));
    this.setState({ loading: 1, page: 1 });
    try {
      const results = await Promise.all(
        (branchIds.length ? branchIds : [0]).map((branch_id) => this.props.client.query({
          query: GET_HOSTEL_ALLOCATIONS,
          variables: {
            institute_id: parseInt(Cookies.get("institutionid")),
            branch_id: branch_id,
          },
          fetchPolicy: "network-only",
        }))
      );
      const rows = results.flatMap((r) => (r.data && r.data.getHostelAllocations) || []);
      this.setState({ rows, loading: 0 });
    } catch (e) {
      console.error("Error fetching hostel allocations:", e);
      this.setState({ rows: [], loading: 0 });
    }
  };

  render() {
    const q = this.state.search.trim().toLowerCase();
    const filtered = q
      ? this.state.rows.filter((r) =>
        [r.student_name, r.admission_no, r.branch_name, r.building_name, r.floor_name, r.room_name]
          .some((v) => (v || "").toString().toLowerCase().includes(q)))
      : this.state.rows;

    // Pagination (pageSize can be "all"), mirroring the Finance dashboard table.
    const pageSizeRaw = this.state.pageSize;
    const pageSize = pageSizeRaw === "all" ? Math.max(1, filtered.length) : (parseInt(pageSizeRaw) || 25);
    const totalPgs = Math.max(1, Math.ceil(filtered.length / pageSize));
    const curPage = Math.min(totalPgs, Math.max(1, this.state.page || 1));
    const startIdx = (curPage - 1) * pageSize;
    const endIdx = Math.min(startIdx + pageSize, filtered.length);
    const paged = filtered.slice(startIdx, endIdx);

    return (
      <div style={S.page}>
        <div style={S.card}>
          <div style={S.cardHeader}>
            <div style={S.iconWrapperList}>
              <i className="fal fa-th-list" />
            </div>
            <div style={S.cardTitleText}>
              <h5 style={S.cardTitle}>Allocation Dashboard</h5>
              <span style={S.cardSubtitle}>All allocated students with their building, floor and room</span>
            </div>
            <span style={S.countChip}>{filtered.length} students</span>
            <button style={{ ...S.addBtn, marginLeft: 12 }} onClick={this.openAllocateModal}>
              <i className="fal fa-plus" /> Allocate Student
            </button>
          </div>

          <div style={S.cardBody}>
            <Row className="align-items-end mb-3">
              {/* No Branch filter — the header (NavbarOne) scopes this dashboard. */}
              <Col lg={4} md={6} sm={12}>
                <label style={S.label}>Search</label>
                <div style={S.searchBox}>
                  <i className="fal fa-search" style={S.searchIcon} />
                  <input
                    type="text"
                    placeholder="Search student, roll no, building…"
                    value={this.state.search}
                    onChange={(e) => this.setState({ search: e.target.value, page: 1 })}
                    style={S.searchInput}
                  />
                </div>
              </Col>
            </Row>

            {this.state.loading == 1 ? (
              <BuildingListShimmer rows={4} />
            ) : filtered.length > 0 ? (
              <div style={{ border: "1px solid #e2e8f0", borderRadius: 12, overflow: "hidden" }}>
                <div style={{ maxHeight: 520, overflowY: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr>
                        <th style={{ ...th, width: 50 }}>#</th>
                        <th style={th}>Student Name</th>
                        <th style={th}>Admission No</th>
                        <th style={th}>Branch</th>
                        <th style={th}>Building</th>
                        <th style={th}>Floor</th>
                        <th style={th}>Room</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paged.map((r, i) => (
                        <tr key={`${r.student_id}-${startIdx + i}`} style={{ background: i % 2 === 0 ? "#fff" : "#fbfcfe" }}>
                          <td style={{ ...td, color: "#94a3b8", fontWeight: 600 }}>{startIdx + i + 1}</td>
                          <td style={{ ...td, fontWeight: 600, color: "#0f172a" }}>
                            <i className="fal fa-user-graduate mr-2" style={{ color: "#4f46e5" }} />{r.student_name}
                          </td>
                          <td style={td}>{r.admission_no || "-"}</td>
                          <td style={td}>{r.branch_name || "-"}</td>
                          <td style={td}><i className="fal fa-building mr-2" style={{ color: "#7c3aed" }} />{r.building_name}</td>
                          <td style={td}>{r.floor_name}</td>
                          <td style={td}><span style={S.miniChip}>{r.room_name}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination footer (Finance-style) */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 14px", flexWrap: "wrap", gap: 10, borderTop: "1px solid #f1f5f9", background: "#fff" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "#6b7280" }}>
                    <span>Rows per page:</span>
                    <select value={pageSizeRaw} onChange={(e) => this.setState({ pageSize: e.target.value === "all" ? "all" : parseInt(e.target.value), page: 1 })} style={{ borderRadius: 6, border: "1.5px solid #e5e7eb", padding: "4px 8px", fontSize: 12, background: "#f9fafb", color: "#374151" }}>
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value="all">All</option>
                    </select>
                    <span style={{ marginLeft: 8 }}>Showing <b>{filtered.length === 0 ? 0 : startIdx + 1}</b>–<b>{endIdx}</b> of <b>{filtered.length}</b></span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    <button onClick={() => this.setState({ page: 1 })} disabled={curPage === 1}
                      style={{ border: "1.5px solid #e5e7eb", background: curPage === 1 ? "#f9fafb" : "#fff", color: curPage === 1 ? "#cbd5e1" : "#374151", borderRadius: 6, padding: "5px 9px", cursor: curPage === 1 ? "not-allowed" : "pointer", fontSize: 12 }}>
                      <i className="fal fa-angle-double-left" />
                    </button>
                    <button onClick={() => this.setState({ page: Math.max(1, curPage - 1) })} disabled={curPage === 1}
                      style={{ border: "1.5px solid #e5e7eb", background: curPage === 1 ? "#f9fafb" : "#fff", color: curPage === 1 ? "#cbd5e1" : "#374151", borderRadius: 6, padding: "5px 9px", cursor: curPage === 1 ? "not-allowed" : "pointer", fontSize: 12 }}>
                      <i className="fal fa-angle-left" />
                    </button>
                    {(() => {
                      const win = 2;
                      const pages = new Set([1, totalPgs, curPage]);
                      for (let p = curPage - win; p <= curPage + win; p++) if (p >= 1 && p <= totalPgs) pages.add(p);
                      const sorted = [...pages].sort((a, b) => a - b);
                      const out = [];
                      let prev = 0;
                      sorted.forEach((p) => {
                        if (p - prev > 1) out.push(<span key={`g${p}`} style={{ padding: "5px 6px", color: "#9ca3af", fontSize: 12 }}>…</span>);
                        const active = p === curPage;
                        out.push(
                          <button key={p} onClick={() => this.setState({ page: p })}
                            style={{ border: active ? "1.5px solid #6366f1" : "1.5px solid #e5e7eb", background: active ? "#6366f1" : "#fff", color: active ? "#fff" : "#374151", borderRadius: 6, padding: "5px 11px", cursor: "pointer", fontSize: 12, fontWeight: active ? 700 : 500, minWidth: 32 }}>
                            {p}
                          </button>
                        );
                        prev = p;
                      });
                      return out;
                    })()}
                    <button onClick={() => this.setState({ page: Math.min(totalPgs, curPage + 1) })} disabled={curPage === totalPgs}
                      style={{ border: "1.5px solid #e5e7eb", background: curPage === totalPgs ? "#f9fafb" : "#fff", color: curPage === totalPgs ? "#cbd5e1" : "#374151", borderRadius: 6, padding: "5px 9px", cursor: curPage === totalPgs ? "not-allowed" : "pointer", fontSize: 12 }}>
                      <i className="fal fa-angle-right" />
                    </button>
                    <button onClick={() => this.setState({ page: totalPgs })} disabled={curPage === totalPgs}
                      style={{ border: "1.5px solid #e5e7eb", background: curPage === totalPgs ? "#f9fafb" : "#fff", color: curPage === totalPgs ? "#cbd5e1" : "#374151", borderRadius: 6, padding: "5px 9px", cursor: curPage === totalPgs ? "not-allowed" : "pointer", fontSize: 12 }}>
                      <i className="fal fa-angle-double-right" />
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div style={S.noData}>
                <i className="fal fa-th-list" style={{ fontSize: 40, marginBottom: 12 }} />
                <h6 style={{ color: "#64748b", margin: 0 }}>No allocations found</h6>
                <span style={{ fontSize: 13, marginTop: 4 }}>Allocate students to rooms to see them here.</span>
              </div>
            )}
          </div>
        </div>

        <AddBuildingModal
          globals={this.props.globals}
          getNewEducationalStudents={[]}
          stateData={{ isEdit: false }}
          show={this.state.allocateModal}
          onHide={() => this.setState({ allocateModal: false })}
          onSaved={() => {
            this.setState({ allocateModal: false });
            this.fetchAllocations();
          }}
        />

        {/* Shown instead of opening the allotment modal when is_edit is missing. This
            dashboard has no delete surface. */}
        <AccessDeniedModal
          show={this.state.accessDeniedShow}
          onHide={this.guard.hideAccessDenied}
        />
      </div>
    );
  }
}

export default withRouter(withApollo(HostelDashboardSection));
